/**
 * Feature Flags Registry
 *
 * Zentrale Verwaltung aller Feature-Flags im Projekt.
 * Flags werden automatisch an Sentry-Events angehängt.
 *
 * DYNAMISCHE FLAGS:
 * Feature-Flags können jetzt dynamisch über die Admin-UI (Setup-Status Seite)
 * aktiviert/deaktiviert werden. Die Werte werden in system_settings gespeichert.
 *
 * PRIORITÄT:
 * 1. DB-Wert aus system_settings (key: 'feature_flags')
 * 2. Statischer Default aus FEATURE_FLAGS (dieses File)
 *
 * Verwendung mit dem useFeatureFlag Hook:
 * ```typescript
 * import { useFeatureFlag } from "@/hooks/useFeatureFlag";
 * import { FEATURE_FLAGS } from "@/lib/featureFlags";
 *
 * const isEnabled = useFeatureFlag('my-feature', FEATURE_FLAGS['my-feature'] ?? false);
 * ```
 *
 * Namenskonvention: kebab-case
 *
 * @see .lovable/memory/features/dynamic-feature-flag-system.md
 */
export const FEATURE_FLAGS = {
  // Microsoft Teams Integration für interaktive Benachrichtigungen
  "teams-notifications": false,
  // Interaktive App-Tour für neue Benutzer
  "app-tour": true,
  // Öffentliche SaaS Landing Page
  "landing-page": false,
  // Öffentliche Preissektion auf der Landing Page
  "public-pricing": false,
  // Offline-Modus für Tablet-Nutzer mit lokaler Datenspeicherung
  "offline-mode": false,
  // Quick-Login Buttons auf der Auth-Seite (nur relevant wenn PRODUCTION_MODE=false)
  "quick-login": true,
  // AI-Assistant-Panel (TES-1128, Epic TES-1124): docked Chat-Panel + Mobile-FAB,
  // angebunden an agent-orchestrator. Default aus, bis ein Provider konfiguriert ist.
  "ai-assistant": false,
  // AI-gestützter Help-Chat-Button (TES-1128): floating Dialog, agent-orchestrator/help-chat.
  "help-chat": false,
} as const;

/**
 * Metadaten für Feature-Flags (UI-Anzeige)
 */
export const FEATURE_FLAG_META: Record<
  string,
  {
    titleKey: string;
    descriptionKey: string;
    category?: string;
  }
> = {
  "teams-notifications": {
    titleKey: "setupStatus.featureFlags.flags.teams-notifications.title",
    descriptionKey: "setupStatus.featureFlags.flags.teams-notifications.description",
    category: "integrations",
  },
  "app-tour": {
    titleKey: "setupStatus.featureFlags.flags.app-tour.title",
    descriptionKey: "setupStatus.featureFlags.flags.app-tour.description",
    category: "ux",
  },
  "landing-page": {
    titleKey: "setupStatus.featureFlags.flags.landing-page.title",
    descriptionKey: "setupStatus.featureFlags.flags.landing-page.description",
    category: "ux",
  },
  "public-pricing": {
    titleKey: "setupStatus.featureFlags.flags.public-pricing.title",
    descriptionKey: "setupStatus.featureFlags.flags.public-pricing.description",
    category: "business",
  },
  "offline-mode": {
    titleKey: "setupStatus.featureFlags.flags.offline-mode.title",
    descriptionKey: "setupStatus.featureFlags.flags.offline-mode.description",
    category: "infrastructure",
  },
  "quick-login": {
    titleKey: "setupStatus.featureFlags.flags.quick-login.title",
    descriptionKey: "setupStatus.featureFlags.flags.quick-login.description",
    category: "dev",
  },
  "ai-assistant": {
    titleKey: "setupStatus.featureFlags.flags.ai-assistant.title",
    descriptionKey: "setupStatus.featureFlags.flags.ai-assistant.description",
    category: "ai",
  },
  "help-chat": {
    titleKey: "setupStatus.featureFlags.flags.help-chat.title",
    descriptionKey: "setupStatus.featureFlags.flags.help-chat.description",
    category: "ai",
  },
};

/**
 * Type für alle verfügbaren Feature-Flag-Namen
 */
export type FeatureFlagName = keyof typeof FEATURE_FLAGS;
